import React, { useCallback, useEffect, useState } from 'react';
import type {
  CommunityVisibilitySettings,
  GradingAnswerSnapshot,
  GradingResultSnapshot,
  MarketProfileState,
  PositionFormData,
  UserProfileFormData,
} from './marketProfileStore';
import {
  computeProgress,
  isQuestionnaireLocked,
  isQuestionnaireSubmitted,
  loadMarketProfile,
  mergeRemoteMarketProfile,
  saveMarketProfile,
  seedMarketProfileIfNeeded,
} from './marketProfileStore';
import { fetchRemoteMarketProfile } from '../../../services/adminCommunityService';
import { useAuth } from '../../../contexts/AuthContext';
import { useCommunityDevMode } from '../CommunityDevModeContext';
import ProfileOverview from './ProfileOverview';
import ProfileScreen from './ProfileScreen';
import PositionQuestionnaire from './PositionQuestionnaire';
import GradingQuestionnaire from './GradingQuestionnaire';
import ProfileReviewScreen from './ProfileReviewScreen';
import QuestionnairePendingReview from './QuestionnairePendingReview';
import MarketDataTab from './marketData/MarketDataTab';

type Screen =
  | 'overview'
  | 'profile'
  | 'position'
  | 'grading'
  | 'review'
  | 'marketData'
  | 'positionPending'
  | 'gradingPending';

type Props = {
  onChanged?: () => void;
  entryScreen?: 'overview' | 'position' | 'grading' | 'marketData';
};

function resolveScreenForState(state: MarketProfileState): Screen {
  const progress = computeProgress(state);
  if (progress.unlocked) return 'marketData';
  if (progress.completed === 2 && !state.confirmedAt) return 'review';
  if (state.position.status === 'pending_review' && state.grading.status !== 'pending_review') {
    return 'positionPending';
  }
  if (state.grading.status === 'pending_review' && state.position.status !== 'pending_review') {
    return 'gradingPending';
  }
  return 'overview';
}

function isQuestionnaireScreen(screen: Screen): screen is 'position' | 'grading' {
  return screen === 'position' || screen === 'grading';
}

function screenAllowed(
  target: Screen,
  positionLocked: boolean,
  gradingLocked: boolean,
): boolean {
  if (target === 'position' && positionLocked) return false;
  if (target === 'grading' && gradingLocked) return false;
  return true;
}

export default function MarketProfileTab({ onChanged, entryScreen = 'overview' }: Props) {
  const { user } = useAuth();
  const userEmail = user?.email ?? null;
  const { marketProfileRevision } = useCommunityDevMode();

  const [state, setState] = useState<MarketProfileState>(() => loadMarketProfile(userEmail));
  const [screen, setScreen] = useState<Screen>(() => {
    const initial = loadMarketProfile(userEmail);
    const positionLocked = isQuestionnaireLocked(initial.position.status);
    const gradingLocked = isQuestionnaireLocked(initial.grading.status);
    if (entryScreen !== 'overview' && screenAllowed(entryScreen, positionLocked, gradingLocked)) {
      return entryScreen;
    }
    return resolveScreenForState(initial);
  });

  const positionLocked = isQuestionnaireLocked(state.position.status);
  const gradingLocked = isQuestionnaireLocked(state.grading.status);

  const navigateTo = useCallback((target: Screen) => {
    if (!screenAllowed(target, positionLocked, gradingLocked)) return;
    setScreen(target);
  }, [positionLocked, gradingLocked]);

  useEffect(() => {
    if (entryScreen === 'overview') {
      setScreen('overview');
      return;
    }
    if (screenAllowed(entryScreen, positionLocked, gradingLocked)) {
      setScreen(entryScreen);
    } else {
      setScreen('overview');
    }
  }, [entryScreen, positionLocked, gradingLocked]);

  useEffect(() => {
    if (isQuestionnaireScreen(screen) && !screenAllowed(screen, positionLocked, gradingLocked)) {
      setScreen('overview');
    }
  }, [screen, positionLocked, gradingLocked]);

  useEffect(() => {
    if (marketProfileRevision === 0) return;
    const next = loadMarketProfile(userEmail);
    setState(next);
    setScreen(resolveScreenForState(next));
    onChanged?.();
  }, [marketProfileRevision, userEmail, onChanged]);

  useEffect(() => {
    const seeded = seedMarketProfileIfNeeded(userEmail);
    const next = seeded ?? loadMarketProfile(userEmail);
    setState(next);
    if (seeded && computeProgress(next).completed === 2 && next.confirmedAt) {
      setScreen('marketData');
    }
    if (seeded) onChanged?.();
  }, [userEmail, onChanged]);

  const persist = useCallback((next: MarketProfileState) => {
    setState(next);
    saveMarketProfile(next, userEmail);
    onChanged?.();
    return next;
  }, [onChanged, userEmail]);

  useEffect(() => {
    let cancelled = false;
    const pullRemote = async () => {
      try {
        const remote = await fetchRemoteMarketProfile();
        if (cancelled || !remote) return;
        setState((prev) => {
          const merged = mergeRemoteMarketProfile(prev, remote);
          saveMarketProfile(merged, userEmail);
          const progress = computeProgress(merged);
          if (
            (prev.position.status === 'pending_review' && merged.position.status === 'complete')
            || (prev.grading.status === 'pending_review' && merged.grading.status === 'complete')
          ) {
            setScreen(progress.completed === 2 ? 'review' : 'overview');
          }
          return merged;
        });
      } catch {
        /* offline / API недоступен */
      }
    };
    void pullRemote();
    const hasPending =
      state.position.status === 'pending_review'
      || state.grading.status === 'pending_review';
    if (!hasPending) return () => { cancelled = true; };
    const id = window.setInterval(pullRemote, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [userEmail, state.position.status, state.grading.status]);

  const progress = computeProgress(state);

  const completePosition = (data: PositionFormData) => {
    const ts = new Date().toISOString();
    persist({
      ...state,
      position: {
        status: 'pending_review',
        data,
        updatedAt: ts,
        reviewSubmittedAt: ts,
      },
      confirmedAt: null,
    });
    setScreen('positionPending');
  };

  const completeGrading = (result: GradingResultSnapshot, answers: GradingAnswerSnapshot[]) => {
    const ts = new Date().toISOString();
    persist({
      ...state,
      grading: {
        status: 'pending_review',
        result,
        answers,
        updatedAt: ts,
        reviewSubmittedAt: ts,
      },
      confirmedAt: null,
    });
    setScreen('gradingPending');
  };

  const confirmProfile = () => {
    persist({ ...state, confirmedAt: new Date().toISOString() });
    setScreen('marketData');
  };

  const saveUserProfile = (data: UserProfileFormData, visibility: CommunityVisibilitySettings) => {
    persist({
      ...state,
      userProfile: {
        data,
        visibility,
        updatedAt: new Date().toISOString(),
      },
    });
  };

  const navHidden =
    screen === 'position'
    || screen === 'grading'
    || screen === 'review'
    || screen === 'positionPending'
    || screen === 'gradingPending';

  const navItems: { id: Screen; label: string; locked?: boolean }[] = [
    { id: 'overview', label: 'Обзор' },
    { id: 'profile', label: 'Профиль' },
    { id: 'position', label: 'Данные о должности', locked: positionLocked },
    { id: 'grading', label: 'Грейдинг', locked: gradingLocked },
    {
      id: 'marketData',
      label: 'Рыночные данные',
      locked: progress.completed < 2,
    },
  ];

  const gradingNotSubmitted = !isQuestionnaireSubmitted(state.grading.status);
  const canOpenPosition = !positionLocked;
  const canOpenGrading = !gradingLocked;

  return (
    <div className="dcf-mp">
      {!navHidden && (
        <nav className="dcf-mp-nav" aria-label="Разделы профиля рынка">
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`dcf-mp-nav__btn ${screen === item.id ? 'dcf-mp-nav__btn--active' : ''} ${item.locked ? 'dcf-mp-nav__btn--locked' : ''}`}
              disabled={item.locked}
              onClick={() => navigateTo(item.id)}
            >
              {item.label}
              {item.locked ? (
                <i className="fas fa-lock dcf-mp-nav__lock" aria-hidden="true" />
              ) : null}
            </button>
          ))}
        </nav>
      )}

      {screen === 'overview' && (
        <ProfileOverview
          state={state}
          onOpenProfile={() => navigateTo('profile')}
          onOpenPosition={() => navigateTo('position')}
          onOpenGrading={() => navigateTo('grading')}
          onOpenReview={() => navigateTo('review')}
          onOpenMarketData={() => navigateTo('marketData')}
          onOpenPositionPending={() => navigateTo('positionPending')}
          onOpenGradingPending={() => navigateTo('gradingPending')}
        />
      )}

      {screen === 'profile' && (
        <ProfileScreen profile={state.userProfile} onSave={saveUserProfile} />
      )}

      {screen === 'position' && canOpenPosition && (
        <PositionQuestionnaire
          initial={state.position.data}
          onComplete={completePosition}
          onBack={() => setScreen('overview')}
        />
      )}

      {screen === 'grading' && canOpenGrading && (
        <GradingQuestionnaire
          onComplete={completeGrading}
          onBack={() => setScreen('overview')}
        />
      )}

      {screen === 'positionPending' && state.position.reviewSubmittedAt && (
        <QuestionnairePendingReview
          kind="position"
          reviewSubmittedAt={state.position.reviewSubmittedAt}
          onBack={() => navigateTo('overview')}
          onContinue={gradingNotSubmitted && canOpenGrading ? () => navigateTo('grading') : undefined}
          continueLabel={gradingNotSubmitted ? 'Пройти грейдинг' : undefined}
        />
      )}

      {screen === 'gradingPending' && state.grading.reviewSubmittedAt && (
        <QuestionnairePendingReview
          kind="grading"
          reviewSubmittedAt={state.grading.reviewSubmittedAt}
          onBack={() => navigateTo('overview')}
        />
      )}

      {screen === 'review' && (
        <ProfileReviewScreen
          state={state}
          onConfirm={confirmProfile}
          onBack={() => navigateTo('overview')}
        />
      )}

      {screen === 'marketData' && (
        <MarketDataTab
          state={state}
          onGoOverview={() => navigateTo('overview')}
          onOpenPosition={canOpenPosition ? () => navigateTo('position') : undefined}
          onOpenGrading={canOpenGrading ? () => navigateTo('grading') : undefined}
          onConfirmReview={() => navigateTo('review')}
        />
      )}
    </div>
  );
}
